import React, { useState, useCallback } from "react";
import { useList, useCreate } from "@refinedev/core";
import { usePortalContact } from "@/hooks/usePortalContact";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { MentionTextarea } from "./MentionTextarea";
import { resolveCommentAuthor, stripContactPrefix } from "@/utils/commentAuthor";

interface VaultCommentPanelProps {
  /** The record ID to attach comments to (e.g. policy ID, document ID) */
  relatedTo: string;
  /** Whether the panel is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
}

export const VaultCommentPanel: React.FC<VaultCommentPanelProps> = ({
  relatedTo,
  open,
  onClose,
}) => {
  const { contactId, contactName } = usePortalContact();
  const { upload: uploadFile, uploading: fileUploading } = useDocumentUpload();
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const { data: commentsData, isLoading, refetch } = useList({
    resource: "comments",
    pagination: { pageSize: 50 },
    filters: [
      { field: "related_to", operator: "eq", value: relatedTo },
    ],
    sorters: [{ field: "createdtime", order: "desc" }],
    queryOptions: { enabled: open && !!relatedTo },
  });

  const { mutateAsync: createComment } = useCreate();

  const comments = (commentsData?.data || []) as any[];

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await createComment({
        resource: "comments",
        values: {
          commentcontent: (contactId || contactName)
            ? `{{contact:${contactId || ""}:${contactName || ""}}}${newComment.trim()}`
            : newComment.trim(),
          related_to: relatedTo,
          assigned_user_id: "19x1",
        },
      });
      setNewComment("");
      refetch();
    } catch {
      // Error handled by notification provider
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    for (const file of files) {
      const result = await uploadFile(file, { linkTo: relatedTo });
      if (result) {
        // Post a comment referencing the uploaded file
        await createComment({
          resource: "comments",
          values: {
            commentcontent: `📎 Allegato: ${result.filename} (${result.id})`,
            related_to: relatedTo,
            assigned_user_id: "19x1",
          },
        });
        refetch();
      }
    }
  }, [uploadFile, relatedTo, createComment, refetch]);

  /** Render comment text with @mentions highlighted */
  const renderCommentContent = (text: string) => {
    // Parse @[Name](id) patterns
    const parts = text.split(/(@\[[^\]]+\]\([^)]+\))/g);
    return parts.map((part, i) => {
      const match = part.match(/^@\[([^\]]+)\]\(([^)]+)\)$/);
      if (match) {
        return (
          <span
            key={i}
            style={{
              color: "var(--vault-accent)",
              fontWeight: 500,
              background: "var(--vault-zone-comment-bg)",
              padding: "1px 4px",
              borderRadius: 2,
            }}
          >
            @{match[1]}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  if (!open) return null;

  return (
    <div
      className="vault-comment-panel"
      style={{ borderLeft: `3px solid ${dragOver ? "var(--vault-zone-upload-border)" : "var(--vault-zone-comment-border)"}` }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleFileDrop}
    >
      <div className="vault-comment-panel-header">
        <span className="vault-label-bracketed">Commenti</span>
        <button className="vault-btn-ghost" onClick={onClose}>
          &#10005;
        </button>
      </div>

      <div className="vault-comment-list">
        {isLoading ? (
          <div className="vault-loading">Loading...</div>
        ) : comments.length === 0 ? (
          <div className="vault-empty" style={{ padding: "2rem" }}>
            Nessun commento
          </div>
        ) : (
          comments.map((c: any) => (
            <div key={c.id} className="vault-comment-item">
              <div className="vault-comment-meta">
                <span className="vault-label-tiny">
                  {resolveCommentAuthor(c).name || contactName || "—"}
                </span>
                <span className="vault-label-tiny">
                  {c.createdtime
                    ? new Date(c.createdtime).toLocaleDateString("it-IT", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : ""}
                </span>
              </div>
              <div className="vault-comment-body">
                {renderCommentContent(stripContactPrefix(c.commentcontent || c.description || ""))}
              </div>
            </div>
          ))
        )}
      </div>

      {dragOver && (
        <div className="vault-zone-upload" style={{ textAlign: "center", padding: "var(--vault-spacing-md)" }}>
          <span className="vault-label-tiny">Rilascia file per allegare</span>
        </div>
      )}
      {fileUploading && (
        <div className="vault-zone-upload" style={{ textAlign: "center", padding: "var(--vault-spacing-xs)" }}>
          <span className="vault-label-tiny">Caricamento file...</span>
        </div>
      )}
      <div className="vault-comment-input-area">
        <MentionTextarea
          value={newComment}
          onChange={setNewComment}
          placeholder="Scrivi un commento, @menziona qualcuno o trascina un file..."
          rows={3}
          onSubmit={handleSubmit}
          disabled={submitting}
        />
        <button
          className="vault-btn"
          onClick={handleSubmit}
          disabled={submitting || !newComment.trim()}
          style={{ minWidth: 120 }}
        >
          <span>{submitting ? "..." : "Invia"}</span>
          <span className="vault-btn-arrow">&#8599;</span>
        </button>
      </div>
    </div>
  );
};
